/**
 * @module
 *
 * @author Jacob Viertel [jv@onscreen.net](mailto:jv@onscreen.net)
 *
 * @since 3.103.0
 */

import {AttributeSetup} from '@acng/frontend-stargazer';
import {allWords} from '@acng/frontend-bounty/std/string.js';
import {remove} from '@acng/frontend-bounty/dom/move.js';
import {guard} from '@acng/frontend-rubicon';

import {checkFlag, AUTH, FLAG} from '../service/flag.js';

/**
 * Remove the element from the template unless all of the given flags apply to
 * the current user.
 *
 * | Flag             | Description              |
 * | ---------------- | ------------------------ |
 * | auth             | is authenticated         |
 * | guest            | is not authenticated     |
 * | native           | is not "international"   |
 * | premium          | `level` is at least `10` |
 * | free             | `level` is below `10`    |
 * | \<empty-string\> | Defaults to "auth".      |
 *
 * The flags are case sensitive.
 *
 * ## Stargazer Lifecycle
 *
 * | Phase   | Description                       |
 * | ------- | --------------------------------- |
 * | Compile | Conditionally remove the element. |
 *
 * The template cache is required to be cleared when a condition changes.
 *
 * ## Formal Syntax
 *
 *     if-user = <user-flags>
 *     <user-flags> = auth || guest || native || premium || free
 *
 * @example
 *
 * ```html
 * <span if-user="auth">You are authenticated.</span>
 * <span if-user="native">You are not "international".</span>
 * <span if-user="guest">You are not authenticated.</span>
 * <span if-user="auth,free">
 *   You are authenticated, but without payment transaction.
 * </span>
 * ```
 *
 * @group DOM Attribute
 *
 * @see {@link USER_LEVEL | `[user-level]`} for tighter check of the user level.
 */
const name = 'if-user';

AttributeSetup(name, () => (element, value, compiler) => {
  value ||= AUTH;

  for (const [flag] of allWords(value)) {
    ASSERT: {
      guard(flag, FLAG, `[${name}="${flag}"] value`);

      if (!(flag in checkFlag)) {
        console.error('Remove', element.outerHTML, compiler);
      }
    }

    if (!checkFlag[flag]?.()) {
      remove(element);
    }
  }
});

export {name as IF_USER};
